import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import FacebookIcon from '@mui/icons-material/Facebook';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { importClients, ImportContactsResult } from '../services/api';

// Additional import sources, not yet wired up. Rendered as "Coming soon" rows.
const UPCOMING_SOURCES: Array<{ key: string; label: string; desc: string; icon: React.ReactNode; color: string }> = [
  { key: 'facebook', label: 'Facebook', desc: 'Lead ads & page contacts', icon: <FacebookIcon />, color: '#1877f2' },
  { key: 'tiktok', label: 'TikTok', desc: 'Lead generation forms', icon: <MusicNoteIcon />, color: '#ff2b55' },
  { key: 'wordpress', label: 'WordPress website', desc: 'Contact form submissions', icon: <LanguageIcon />, color: '#3b82f6' },
  { key: 'google-forms', label: 'Google Forms', desc: 'Form responses', icon: <DescriptionIcon />, color: '#7c3aed' },
  { key: 'whatsapp-chat', label: 'WhatsApp Chat', desc: 'Import from a chat export', icon: <WhatsAppIcon />, color: '#25D366' },
];

// A downloadable CSV template so agents know the exact columns we accept.
const SAMPLE_CSV = [
  'name,phone,email',
  'Sarah Tan,+65 9123 4567,sarah.tan@example.com',
  'John Lim,+65 8234 5678,john.lim@example.com',
  'Michelle Wong,+65 9345 6789,',
].join('\n');

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'super-connector-contacts-sample.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface ParsedContact {
  name: string;
  mobile: string;
  selected: boolean;
}

// Browser Contact Picker — Android Chrome & some mobile browsers only.
const contactPickerSupported = (): boolean =>
  typeof navigator !== 'undefined'
  && 'contacts' in navigator
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  && typeof (navigator as any).contacts?.select === 'function';

// Minimal CSV parser: supports quoted fields and commas inside quotes.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else { field += ch; }
  }
  if (field !== '' || row.length) { row.push(field); if (row.some((c) => c.trim() !== '')) rows.push(row); }
  return rows;
}

// Map CSV rows to contacts. Uses a header row when name/phone columns are
// recognised; otherwise assumes column 0 = name, column 1 = phone.
function rowsToContacts(rows: string[][]): ParsedContact[] {
  if (rows.length === 0) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const nameIdx = header.findIndex((h) => h === 'name' || h === 'full name' || h === 'contact');
  const phoneIdx = header.findIndex((h) => ['phone', 'mobile', 'number', 'tel', 'phone number'].includes(h));
  const hasHeader = nameIdx !== -1 || phoneIdx !== -1;
  const body = hasHeader ? rows.slice(1) : rows;
  const ni = nameIdx === -1 ? 0 : nameIdx;
  const pi = phoneIdx === -1 ? 1 : phoneIdx;
  return body
    .map((r) => ({ name: (r[ni] || '').trim(), mobile: (r[pi] || '').trim(), selected: true }))
    .filter((c) => c.name || c.mobile);
}

export default function ImportContactsDialog({
  open, onClose, onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (result: ImportContactsResult) => void;
}) {
  const [contacts, setContacts] = useState<ParsedContact[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [engagementActive, setEngagementActive] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reset = () => { setContacts([]); setError(null); setEngagementActive(false); };
  const close = () => { reset(); onClose(); };

  const mergeIn = (incoming: ParsedContact[]) => {
    setContacts((prev) => {
      const seen = new Set(prev.map((c) => c.mobile || c.name));
      const fresh = incoming.filter((c) => !seen.has(c.mobile || c.name));
      return [...prev, ...fresh];
    });
  };

  const pickFromPhone = async () => {
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const results = await (navigator as any).contacts.select(['name', 'tel'], { multiple: true });
      const parsed: ParsedContact[] = (results || [])
        .map((r: { name?: string[]; tel?: string[] }) => ({
          name: (r.name && r.name[0]) || '',
          mobile: (r.tel && r.tel[0]) || '',
          selected: true,
        }))
        .filter((c: ParsedContact) => c.name || c.mobile);
      if (parsed.length === 0) { setError('No contacts selected.'); return; }
      mergeIn(parsed);
    } catch (e) {
      // User cancelling the picker throws; only surface real errors.
      if (e instanceof Error && !/abort|cancel/i.test(e.message)) setError(e.message);
    }
  };

  const onFile = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const parsed = rowsToContacts(parseCsv(text));
      if (parsed.length === 0) { setError('No rows found in that file.'); return; }
      mergeIn(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read file');
    }
  };

  const toggle = (idx: number) => setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, selected: !c.selected } : c)));
  const selectedCount = contacts.filter((c) => c.selected).length;

  const doImport = async () => {
    const chosen = contacts.filter((c) => c.selected);
    if (chosen.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await importClients(
        chosen.map((c) => ({ name: c.name, mobile: c.mobile })),
        engagementActive,
      );
      onImported(result);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && close()} fullWidth maxWidth="sm"
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}>
      <DialogTitle fontWeight={700}>Import contacts</DialogTitle>
      <DialogContent>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 1.5 }}>
          Pull contacts from your phone, or upload a CSV (columns: name, phone, email). Duplicates by phone number are skipped.{' '}
          <Typography
            component="span"
            variant="caption"
            onClick={downloadSampleCsv}
            sx={{ color: '#ed8936', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}
          >
            <DownloadIcon sx={{ fontSize: 14 }} />Download sample CSV
          </Typography>
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {contactPickerSupported() && (
            <Button variant="outlined" startIcon={<ContactPhoneIcon />} onClick={pickFromPhone}
              sx={{ textTransform: 'none', borderColor: '#ed8936', color: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: '#ed893611' } }}>
              From phone contacts
            </Button>
          )}
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}
            sx={{ textTransform: 'none' }}>
            Upload CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
        </Box>

        {!contactPickerSupported() && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
            Tip: picking from your phone's address book is available on mobile (Android Chrome). On desktop, use CSV.
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}

        <FormControlLabel
          sx={{ mx: 0, mb: 1.5, alignItems: 'flex-start' }}
          control={(
            <Switch
              checked={engagementActive}
              onChange={(_, on) => setEngagementActive(on)}
              disabled={busy}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
              }}
            />
          )}
          label={(
            <Box sx={{ pt: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>7-Day Engagement</Typography>
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block' }}>
                {engagementActive
                  ? 'Every client in this import will be enrolled in 7-Day Engagement.'
                  : 'Imported clients will not be enrolled in 7-Day Engagement.'}
              </Typography>
            </Box>
          )}
        />

        {contacts.length > 0 && (
          <>
            <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 0.5 }}>
              {selectedCount} of {contacts.length} selected
            </Typography>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 1 }}>
              {contacts.map((c, idx) => (
                <Box key={`${c.mobile}-${idx}`}
                  onClick={() => toggle(idx)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(237,137,54,0.08)' } }}>
                  <Checkbox checked={c.selected} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: '#ed8936' } }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{c.name || '(no name)'}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>{c.mobile || 'no phone'}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* More import sources — not yet available (disabled) */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', mb: 1.25 }}>
          More import sources
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {UPCOMING_SOURCES.map((s) => (
            <Box
              key={s.key}
              aria-disabled
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
                border: '1px solid var(--color-border)', borderRadius: 2,
                opacity: 0.55, cursor: 'not-allowed', userSelect: 'none',
              }}
            >
              {/* Coloured icon tile */}
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: `${s.color}1f`, color: s.color,
                }}
              >
                {s.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.3 }} noWrap>
                  {s.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }} noWrap>
                  {s.desc}
                </Typography>
              </Box>
              {/* Coming soon badge */}
              <Box
                sx={{
                  px: 1, py: 0.4, borderRadius: 999, flexShrink: 0,
                  background: 'linear-gradient(135deg, #ed8936, #f6ad55)',
                  color: '#fff', fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                  boxShadow: '0 1px 3px rgba(237,137,54,0.45)',
                }}
              >
                Coming soon
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={doImport} disabled={busy || selectedCount === 0}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
          sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
          {busy ? 'Importing...' : `Import ${selectedCount || ''}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
